/**
 * What a canvasser actually steers on, derived from knocks already in memory.
 *
 * The old header said "12 / 40 doors knocked". Doors is effort, not result:
 * `not_home` is most of a shift on most turfs, so twelve doors that produced
 * five real conversations reads as a 30% failure when it was a good hour.
 * This module derives the numbers that answer "how is it going" honestly —
 * conversations, the outcome mix behind them, and pace — without inventing a
 * target to miss.
 *
 * ⚠️ ORIENTATION, NOT SCORE. Nothing here returns a percentage of a quota, a
 * streak, a goal, or a projection. The AAR copy in this app promises "honest
 * reflections beat perfect numbers", and the failure mode of a stat bar that
 * reads as a target is a volunteer logging a not-home as a contact to make it
 * move. A number that can be gamed will be. These are counts and rates with
 * units attached, and that is the whole design.
 */
import type { KnockbackOutcome } from '@/types';

/**
 * Outcome → category, mirroring `_KNOCK_STATE_BY_OUTCOME` in
 * `backend/app/routers/canvass.py`.
 *
 * COME_BACK and DO_NOT_CONTACT collapse into `talked` because both mean "we
 * spoke to somebody and learned something" — a do-not-contact is a person
 * telling us to stop, which only a person can do. Their distinct follow-up
 * meaning lives on the knock row, not here.
 *
 * This is a MIRROR, not a second taxonomy: if the backend map ever grows an
 * outcome, this one has to grow with it, which is why the two are named the
 * same thing and this comment points at the file.
 */
export const KNOCK_CATEGORY_BY_OUTCOME: Record<KnockbackOutcome, KnockCategory> = {
  talked: 'talked',
  not_home: 'not_home',
  refused: 'refused',
  moved: 'moved',
  come_back: 'talked',
  do_not_contact: 'talked',
};

export type KnockCategory = 'talked' | 'not_home' | 'refused' | 'moved';

export interface OutcomeMix {
  /** Doors where somebody came to it and talked with us. The primary number. */
  talked: number;
  not_home: number;
  /** Somebody answered and shut it down. A contact, but not a conversation —
   *  the backend keeps it a separate state and so do we. */
  refused: number;
  moved: number;
  /** Knocks counted, i.e. doors worked this shift. */
  total: number;
}

const EMPTY_MIX: OutcomeMix = {
  talked: 0,
  not_home: 0,
  refused: 0,
  moved: 0,
  total: 0,
};

/** The shape both a server knock and a queued knock body satisfy. */
export interface KnockLike {
  route_stop_id: number;
  outcome: KnockbackOutcome;
}

/**
 * Count outcomes for the shift, one per door.
 *
 * Two inputs because a knock logged in a dead zone is just as real as one the
 * server has: `queued` are the offline rows still sitting in IndexedDB. If the
 * mix ignored them it would total *less* than the "doors knocked" count beside
 * it — which merges both — and a volunteer would be looking at two numbers that
 * disagree about what they just did.
 *
 * Keyed by `route_stop_id`, server rows first, so the brief window where a
 * knock exists in both places (mid-drain) counts once. Within `served`, the
 * first entry for a stop wins: the list is newest-first, and the latest knock
 * is the one that describes the door.
 */
export function summarizeOutcomes(
  served: KnockLike[],
  queued: KnockLike[] = [],
): OutcomeMix {
  const byStop = new Map<number, KnockbackOutcome>();
  for (const k of served) {
    if (!byStop.has(k.route_stop_id)) byStop.set(k.route_stop_id, k.outcome);
  }
  for (const q of queued) {
    if (!byStop.has(q.route_stop_id)) byStop.set(q.route_stop_id, q.outcome);
  }

  const mix: OutcomeMix = { ...EMPTY_MIX };
  for (const outcome of byStop.values()) {
    const category = KNOCK_CATEGORY_BY_OUTCOME[outcome];
    // An outcome this build has never heard of still counts as a worked door.
    // Dropping it would make the mix quietly disagree with the door count.
    if (category) mix[category] += 1;
    mix.total += 1;
  }
  return mix;
}

/**
 * Doors per hour, or null when the sample is too thin to mean anything.
 *
 * Four doors in the first six minutes is 40/hr and it is noise; showing it
 * would make the number bounce for the first half hour and teach the volunteer
 * to ignore the whole bar. Below either floor the caller omits the element.
 */
export function doorsPerHour(
  doors: number,
  startedAt: string | null | undefined,
  now: Date = new Date(),
  minMinutes = 15,
  minDoors = 3,
): number | null {
  if (!startedAt) return null;
  const started = new Date(startedAt).getTime();
  if (!Number.isFinite(started)) return null;
  const minutes = (now.getTime() - started) / 60_000;
  // A clock-skewed device can report a shift that started in the future.
  if (minutes < minMinutes || doors < minDoors) return null;
  return Math.round((doors / minutes) * 60 * 10) / 10;
}
